/* Visão geral: distribuição entre usuários e a matriz usuário × categoria.
 *
 * Métricas sempre por usuário, com mediana entre eles — nunca agregado global.
 * O usuário 1676bc80 sozinho carrega R$83,6M de R$210M; um "% em OUTROS" global
 * seria a fotografia de um investidor pesado, não do produto. */

import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { api, qs } from "../lib/api";
import type { DistributionRow, MatrixPayload } from "../lib/api";
import { cell, compact, shortName } from "../lib/format";
import { StripLabels, StripPlot } from "../charts/StripPlot";
import { HeatMatrix } from "../charts/HeatMatrix";
import { Card, ChartFrame, Empty, ErrorState, PageHead, Skeleton, Tiles, useTooltip }
  from "../components/ui";

export function Overview() {
  const [params, setParams] = useSearchParams();
  const navigate = useNavigate();
  const plotRef = useRef<HTMLDivElement>(null);
  const [plotWidth, setPlotWidth] = useState(560);
  const { setTip, tipNode } = useTooltip();

  const window_ = Number(params.get("janela") || 6);
  const query = params.get("q") || "";
  const logScale = params.get("escala") !== "linear";
  const heat = params.get("heat") !== "0";

  const set = (key: string, value: string) => {
    const next = new URLSearchParams(params);
    if (value) next.set(key, value); else next.delete(key);
    setParams(next, { replace: true });
  };

  // O strip plot é medido em pixels, então precisa da largura real do contêiner.
  useEffect(() => {
    if (!plotRef.current) return;
    const observer = new ResizeObserver(([entry]) =>
      setPlotWidth(Math.max(280, entry.contentRect.width)));
    observer.observe(plotRef.current);
    return () => observer.disconnect();
  }, []);

  const distribution = useQuery({
    queryKey: ["distribution", window_, query],
    queryFn: () => api<{ categories: DistributionRow[] }>(
      `/api/overview/distribution${qs({ window: window_, q: query })}`),
    placeholderData: (previous) => previous,
  });
  const matrix = useQuery({
    queryKey: ["matrix", window_],
    queryFn: () => api<MatrixPayload>(`/api/overview/matrix${qs({ window: window_ })}`),
    placeholderData: (previous) => previous,
  });
  const reconciliation = useQuery({
    queryKey: ["reconciliation"],
    queryFn: () => api<{
      by_flow: { flow: string; mass: number; rows: number }[]; gross: number;
    }>("/api/quality/reconciliation"),
  });

  const rows = distribution.data?.categories ?? [];

  // Nao ha tile de taxa de poupanca aqui, e a ausencia e deliberada. No agregado
  // ela da -351,7%: nao porque alguem gasta 4x o que ganha, mas porque o corpus
  // captura R$3,7M de receita contra R$16,9M de despesa. O numero mediria a
  // cobertura da importacao e seria lido como o comportamento de 29 pessoas.
  // Por usuario ela continua existindo, com piso de renda - ver /api/users/{id}/summary.
  const tiles = useMemo(() => {
    if (!reconciliation.data) return [];
    const flows = Object.fromEntries(
      reconciliation.data.by_flow.map((f) => [f.flow, f]));
    return [
      { key: "despesa", label: "Despesa", unit: "BRL", flow: "DESPESA",
        value: flows.DESPESA?.mass ?? null,
        detail: `${(flows.DESPESA?.rows ?? 0).toLocaleString("pt-BR")} descrições` },
      { key: "receita", label: "Receita", unit: "BRL", flow: "RECEITA",
        value: flows.RECEITA?.mass ?? null,
        detail: `${(flows.RECEITA?.rows ?? 0).toLocaleString("pt-BR")} descrições` },
      { key: "movim", label: "Movimentação", unit: "BRL", flow: "MOVIMENTACAO",
        value: flows.MOVIMENTACAO?.mass ?? null,
        detail: "fora do total, por definição" },
      { key: "bruto", label: "Massa bruta", unit: "BRL",
        value: reconciliation.data.gross,
        detail: "tudo somado, inclusive movimentação" },
    ];
  }, [reconciliation.data]);

  // O hover fala do agregado, e so do agregado: media, mediana e os quartis daquela
  // categoria. O valor de um usuario especifico saiu de proposito -- ele ja tem pagina
  // propria, a um clique no ponto, e no lugar dele dizia o obvio (um ponto vale o que
  // vale) escondendo o que a linha inteira tem a dizer.
  const hoverRow: Parameters<typeof StripPlot>[0]["onHoverRow"] = (info) =>
    setTip(info && {
      text: `${shortName(info.row.key)} · média R$ ${compact(info.row.mean)}`
          + ` · mediana R$ ${compact(info.row.median)}`
          + ` · p25–p75 R$ ${compact(info.row.p25)}–${compact(info.row.p75)}`
          + ` · ${info.row.n} de ${info.row.total_users} usuários`,
      x: info.x, y: info.y,
    });

  return (
    <>
      <PageHead eyebrow={`média de ${window_} meses`}
                title="Visão geral">
        Distribuição entre os usuários, por categoria. A mediana e os quartis dizem o que é
        típico; os pontos dizem quem escapa disso.
      </PageHead>

      <Tiles items={tiles} />

      <div className="controls" style={{ marginTop: 18 }}>
        <label>janela
          <select value={window_} onChange={(e) => set("janela", e.target.value)}>
            <option value="3">3 meses</option>
            <option value="6">6 meses</option>
            <option value="12">12 meses</option>
          </select>
        </label>
        <label>escala
          <select value={logScale ? "log" : "linear"}
                  onChange={(e) => set("escala", e.target.value === "log" ? "" : "linear")}>
            <option value="log">log (comparável entre categorias)</option>
            <option value="linear">linear</option>
          </select>
        </label>
        <input type="search" placeholder="Filtrar categoria…" value={query}
               aria-label="Filtrar categoria" onChange={(e) => set("q", e.target.value)} />
      </div>

      <ChartFrame
        title="Distribuição entre usuários"
        sub="caixa do 1º ao 3º quartil, traço cheio na mediana, tracejado na média — passe o mouse na linha para os números; clique num ponto para abrir aquele usuário"
        guide="niveis"
        tableHeader={["categoria", "usuários", "p25", "mediana", "média", "p75", "máximo"]}
        tableRows={rows.map((r) =>
          [shortName(r.key), r.n, r.p25, r.median, r.mean, r.p75, r.max])}
      >
        {distribution.isPending && <Skeleton rows={12} />}
        {distribution.error && (
          <ErrorState error={distribution.error} onRetry={() => distribution.refetch()} />
        )}
        {distribution.data && !rows.length && (
          <Empty onClear={() => setParams(new URLSearchParams())}>
            Nenhuma categoria corresponde ao filtro.
          </Empty>
        )}
        {!!rows.length && (
          <div style={{ display: "grid", gridTemplateColumns: "210px minmax(0,1fr)", gap: 14 }}>
            <StripLabels rows={rows} onHoverRow={hoverRow} />
            <div ref={plotRef}>
              <StripPlot rows={rows} width={plotWidth} logScale={logScale}
                         onPick={(userId) => navigate(`/usuarios/${userId}`)}
                         onHoverRow={hoverRow} />
            </div>
          </div>
        )}
      </ChartFrame>

      <div style={{ marginTop: 18 }}>
        <Card
          title="Matriz usuário × categoria"
          sub={`média mensal na janela de ${window_} meses · a primeira linha é a média geral por categoria`}
          right={
            <>
              <label className="switch">
                <input type="checkbox" checked={heat}
                       onChange={(e) => set("heat", e.target.checked ? "" : "0")} />
                escala de calor
              </label>
              <a className="btn" href={`/api/overview/matrix.csv${qs({ window: window_ })}`}>
                baixar CSV
              </a>
            </>
          }
        >
          {matrix.isPending && <Skeleton rows={10} />}
          {matrix.data && (
            <HeatMatrix
              firstColumnLabel="usuário"
              columns={matrix.data.columns.map((c) => ({ key: c.key, label: shortName(c.key), flow: c.flow }))}
              rows={matrix.data.rows.map((r) => ({
                key: r.user_id, label: r.short_id, values: r.values, total: r.total,
              }))}
              normalize="column"
              columnMax={matrix.data.column_max}
              heat={heat}
              onPickRow={(userId) => navigate(`/usuarios/${userId}`)}
              summaryRows={[{
                key: "media",
                label: `média dos ${matrix.data.users}`,
                values: matrix.data.column_mean,
                total: matrix.data.total_mean,
                title: matrix.data.columns.map((c, i) =>
                  `${shortName(c.key)}: média R$ ${cell(matrix.data!.column_mean[i])}`
                  + ` · entre os ${matrix.data!.column_users[i]} que têm `
                  + `R$ ${cell(matrix.data!.column_mean_active[i])}`
                  + ` · mediana R$ ${cell(matrix.data!.column_median[i])}`).join("\n"),
              }]}
            />
          )}
        </Card>
      </div>

      {tipNode}
    </>
  );
}
